<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessageBox } from "element-plus";
import { useAuthStore } from "../stores/auth";
import { primaryMenuItems, settingsMenuItems } from "../menu";

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();

const canShow = (item: (typeof primaryMenuItems)[number]): boolean => (
  auth.canAccess(item.menuKey)
);
const menu = computed(() => primaryMenuItems.filter(canShow));
const settingsMenu = computed(() => settingsMenuItems.filter(canShow));
const settingsExpanded = ref(true);

const activePath = computed(() => {
  const match = [...menu.value, ...settingsMenu.value].find((item) => route.path.startsWith(item.path));
  return match?.path ?? "/dashboard";
});
watch(() => route.path, (value) => {
  if (settingsMenu.value.some((item) => value.startsWith(item.path))) settingsExpanded.value = true;
});

async function logout(): Promise<void> {
  await ElMessageBox.confirm("确认退出当前账号？", "退出登录", { type: "warning" });
  await auth.logout();
  await router.replace("/login");
}
</script>

<template>
  <div class="shell">
    <aside class="sidebar">
      <div class="brand">
        <img class="brand-logo" src="/brand/edianlian-logo.png" alt="e电联" />
        <div>
          <strong>云端电表平台</strong>
        </div>
      </div>
      <nav>
        <router-link
          v-for="item in menu"
          :key="item.path"
          :to="item.path"
          :class="{ active: activePath === item.path }"
        >
          <span class="menu-mark">{{ item.mark }}</span>
          {{ item.label }}
        </router-link>
        <div v-if="settingsMenu.length" class="nav-group">
          <button class="nav-parent" type="button" :class="{ active: settingsMenu.some((item) => activePath === item.path) }" @click="settingsExpanded=!settingsExpanded">
            <span class="menu-mark">⚙</span><span>系统设置</span><small>{{ settingsExpanded ? "⌃" : "⌄" }}</small>
          </button>
          <div v-show="settingsExpanded" class="submenu">
            <router-link v-for="item in settingsMenu" :key="item.path" :to="item.path" :class="{ active: activePath === item.path }">{{ item.label }}</router-link>
          </div>
        </div>
      </nav>
    </aside>
    <main class="main">
      <header class="topbar">
        <div>
          <span class="breadcrumb">云端电表平台</span>
          <span class="slash">/</span>
          <strong>{{ route.meta.title }}</strong>
        </div>
        <div class="user-area">
          <span class="personnel-level">{{ auth.user?.isSystemAdmin ? "系统管理员" : auth.canAccess("personnel") ? "管理人员" : "普通人员" }}</span>
          <button class="user-button" type="button" @click="logout">
            <span class="avatar">{{ auth.user?.displayName.slice(0, 1) }}</span>
            <span>{{ auth.user?.displayName }}</span>
            <small>退出</small>
          </button>
        </div>
      </header>
      <router-view />
    </main>
  </div>
</template>

<style scoped>
.shell { display: flex; min-height: 100vh; }
.sidebar {
  position: fixed; inset: 0 auto 0 0; z-index: 20; width: 236px;
  display: flex; flex-direction: column; color: #d6deec; background:
    radial-gradient(circle at 20% 5%, rgba(52,120,246,.2), transparent 30%),
    linear-gradient(180deg, #111c30 0%, #09101d 100%);
}
.brand { display: flex; align-items: center; gap: 12px; height: 74px; padding: 0 22px; border-bottom: 1px solid rgba(255,255,255,.07); }
.brand-logo { width: 62px; height: 34px; object-fit: contain; }
.brand strong { display: block; color: white; font-size: 15px; letter-spacing: .02em; }
nav { display: flex; flex: 1; flex-direction: column; gap: 4px; padding: 22px 14px; overflow-y:auto; }
nav a { display: flex; align-items: center; gap: 13px; height: 45px; padding: 0 14px; border-radius: 9px; color: #9da9bc; text-decoration: none; transition: .18s ease; }
nav a:hover { color: white; background: rgba(255,255,255,.055); }
nav a.active { color: white; background: linear-gradient(90deg, rgba(52,120,246,.95), rgba(52,120,246,.72)); box-shadow: 0 8px 24px rgba(36,105,232,.25); }
.menu-mark { display: grid; width: 19px; height: 19px; place-items: center; font-size: 19px; }
.nav-group{margin-top:2px}.nav-parent{display:flex;align-items:center;width:100%;height:45px;padding:0 14px;border:0;border-radius:9px;color:#9da9bc;background:transparent;font:inherit;cursor:pointer;transition:.18s ease}.nav-parent:hover,.nav-parent.active{color:white;background:rgba(255,255,255,.055)}.nav-parent>span:nth-child(2){margin-left:13px}.nav-parent small{margin-left:auto;color:#71809a}.submenu{display:flex;flex-direction:column;gap:2px;margin:3px 0 5px 20px;padding-left:17px;border-left:1px solid rgba(255,255,255,.1)}.submenu a{height:36px;padding:0 12px;font-size:12px}.submenu a.active{background:rgba(52,120,246,.28);box-shadow:none}
.main { width: calc(100% - 236px); min-height: 100vh; margin-left: 236px; }
.topbar { position: sticky; top: 0; z-index: 15; display: flex; align-items: center; justify-content: space-between; height: 72px; padding: 0 32px; border-bottom: 1px solid #e3e8ef; background: rgba(255,255,255,.94); backdrop-filter: blur(14px); }
.breadcrumb { color: #8a95a7; font-size: 13px; } .slash { margin: 0 9px; color: #c0c7d1; } .topbar strong { font-size: 14px; }
.user-area { display: flex; align-items: center; gap: 14px; }
.personnel-level { padding: 5px 9px; border: 1px solid #dbe5f8; border-radius: 6px; color: #4373c5; background: #f4f8ff; font-size: 11px; }
.user-button { display: flex; align-items: center; gap: 9px; padding: 4px 0; border: 0; color: #263248; background: none; cursor: pointer; }
.user-button small { color: #9aa3b2; }
.avatar { display: grid; width: 34px; height: 34px; place-items: center; border-radius: 10px; color: white; background: linear-gradient(135deg,#3f7ff4,#2756b8); font-weight: 700; }
</style>
